import React, { createContext, useContext, useEffect, useState } from "react";

export enum MatcherView {
  MAIN = "MAIN",
  PREVIEW = "PREVIEW",
  RESULTS = "RESULTS",
  SUMMARY = "SUMMARY",
  DUPLICATES = "DUPLICATES",
  UNMATCH = "UNMATCH",
}

type Duplicate = {
  FX_UNIQUE_ID: string;
  NAME: string;
  COUNT: number;
};

export type PhaseData = {
  cubeId: string;
  phase: number;
  tableName: string;
  matcherTable: string;
  headers: string[][];
  identifier: string;
  totalRecords: number;
  matchedCount: number;
  duplicateCount: number;
  duplicates?: Duplicate[];
};

interface MatcherContextType {
  phaseData: PhaseData;
  activeMatcherView: MatcherView;
  changeView: (view: MatcherView) => void;
  setPhaseData: React.Dispatch<React.SetStateAction<PhaseData>>;
  phaseHistory: PhaseData[];
  setPhaseHistory: React.Dispatch<React.SetStateAction<PhaseData[]>>;
}

const MatcherContext = createContext<MatcherContextType | undefined>(undefined);

const initial: PhaseData = {
  cubeId: "",
  phase: -1,
  tableName: "",
  matcherTable: "",
  headers: [],
  identifier: "",
  totalRecords: -100,
  matchedCount: -1,
  duplicateCount: -1,
};

export function MatcherProvider({ children }: { children: React.ReactNode }): JSX.Element {
  const [phaseData, setPhaseData] = useState<PhaseData>(initial);
  const [phaseHistory, setPhaseHistory] = useState<PhaseData[]>([]);
  const [activeMatcherView, setActiveMatcherView] = useState<MatcherView>(MatcherView.MAIN);

  useEffect(() => {
    if (activeMatcherView === MatcherView.MAIN) {
      setPhaseHistory([]);
      setPhaseData(initial);
    }
  }, [activeMatcherView]);

  return (
    <MatcherContext.Provider
      value={{
        phaseData,
        setPhaseData,
        phaseHistory,
        setPhaseHistory,
        activeMatcherView,
        changeView: (view) => setActiveMatcherView(view),
      }}
    >
      {children}
    </MatcherContext.Provider>
  );
}

export const useMatcher = (): MatcherContextType => {
  const context = useContext(MatcherContext);
  if (!context) {
    throw new Error("useMatcher must be used within a MatcherProvider");
  }
  return context;
};
